import React from 'react';

import PropTypes from 'prop-types';

import { variantSwitch } from '@adsk/alloy-react-helpers';
import {
  AlertInformationIcon,
  AlertWarningIcon,
  CheckmarkCircleIcon,
  RenderIconType,
  XCircleIcon,
} from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { NOTIFICATION_STATUSES, NotificationStatus } from './helpers/consts';

const mapNotificationStatusToIconRenderer: Record<string, RenderIconType> = {
  [NOTIFICATION_STATUSES.NONE]: () => null,
  [NOTIFICATION_STATUSES.INFO]: (p) => <AlertInformationIcon {...p} />,
  [NOTIFICATION_STATUSES.SUCCESS]: (p) => <CheckmarkCircleIcon {...p} />,
  [NOTIFICATION_STATUSES.WARNING]: (p) => <AlertWarningIcon {...p} />,
  [NOTIFICATION_STATUSES.ERROR]: (p) => <XCircleIcon {...p} />,
};

type NotificationIconProps = StylableComponent<HTMLDivElement> & {
  status?: NotificationStatus;
};

const NotificationIcon = React.forwardRef<
  HTMLDivElement,
  NotificationIconProps
>(
  (
    { style, className, status = NOTIFICATION_STATUSES.INFO, ...props },
    ref,
  ) => {
    const renderIcon = mapNotificationStatusToIconRenderer[status];
    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            width: 24,
            height: 24,
          },
          variantSwitch({
            statement: status,
            variants: {
              [NOTIFICATION_STATUSES.INFO]: {
                color: theme.tokens.colors.icon.information.value,
              },
              [NOTIFICATION_STATUSES.SUCCESS]: {
                color: theme.tokens.colors.icon.success.value,
              },
              [NOTIFICATION_STATUSES.WARNING]: {
                color: theme.tokens.colors.icon.warning.value,
              },
              [NOTIFICATION_STATUSES.ERROR]: {
                color: theme.tokens.colors.icon.error.value,
              },
            },
          }),

          style,
        ]}
      >
        {renderIcon?.({})}
      </div>
    );
  },
);

NotificationIcon.displayName = 'Notifications';

NotificationIcon.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Notification status */
  status: PropTypes.oneOf(Object.values(NOTIFICATION_STATUSES)),
};

export default NotificationIcon;
